import * as PrimitiveDialog from '@radix-ui/react-dialog'

import { Button, type ButtonProps } from '~/components/button'
import type { TIconProps } from '~/components/icon'

import { cn } from '~/utils/cn'

type RightActionButton = ButtonProps & {
  icon?: TIconProps['symbol']
  hidden?: boolean
}

type SideSheetHeaderProps = {
  title: string
  description?: string
  showBackButton?: boolean
  onBackButton?(): void
  rightActionButton?: RightActionButton
}

function SideSheetHeader({
  title,
  description,
  showBackButton = true,
  onBackButton,
  rightActionButton,
}: SideSheetHeaderProps) {
  const { icon, hidden, ...rightButtonProps } = rightActionButton ?? {}

  return (
    <header
      className={cn(
        'flex w-full items-center justify-between gap-2 px-[var(--padding-inset-fourpulse)] py-[var(--padding-inset-threepulse)]'
      )}
      data-slot="side-sheet-header"
    >
      <div className="inline-flex items-center gap-2 overflow-hidden">
        {showBackButton && (
          <Button.Root
            variant="neutral-tertiary"
            size="ml"
            onClick={onBackButton}
          >
            <Button.Icon symbol="rdicon-chevron-left" />
          </Button.Root>
        )}

        <hgroup className={cn('max-w-full *:line-clamp-2')}>
          <PrimitiveDialog.Title
            className={cn(
              'font-bold font-sans leading-7 tracking-normal',
              'text-[length:var(--font-size-fivepulse)] text-[var(--color-text-neutral-default)]'
            )}
          >
            {title}
          </PrimitiveDialog.Title>

          <PrimitiveDialog.Description
            className={cn(
              'font-normal font-sans leading-[140%]',
              'text-[length:var(--font-size-threeandhalfpulse)] text-[var(--color-text-neutral-alternative)]'
            )}
          >
            {description}
          </PrimitiveDialog.Description>
        </hgroup>
      </div>

      {rightButtonProps && hidden && (
        <Button.Root
          {...rightActionButton}
          className={cn('shrink-0')}
          variant="neutral-tertiary"
          size="ml"
        >
          <Button.Icon symbol={icon || 'rdicon-default'} />
        </Button.Root>
      )}
    </header>
  )
}

export { SideSheetHeader }
